const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
let loaderPromise = null;

const ensureRazorpayLoaded = async () => {
  if (typeof window === 'undefined') {
    throw new Error('Razorpay is not available in this environment');
  }

  if (window.Razorpay) {
    return;
  }

  if (!loaderPromise) {
    loaderPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = RAZORPAY_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
      document.head.appendChild(script);
    }).catch((error) => {
      loaderPromise = null;
      throw error;
    });
  }

  await loaderPromise;

  if (!window.Razorpay) {
    throw new Error('Razorpay SDK did not initialize correctly');
  }
};

export async function openRazorpayCheckout(options) {
  await ensureRazorpayLoaded();

  return new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      reject(new Error('Razorpay SDK unavailable'));
      return;
    }

    const checkoutOptions = {
      key: options.key,
      amount: options.amount,
      currency: options.currency,
      name: options.name,
      description: options.description,
      image: options.image,
      order_id: options.order_id || options.orderId,
      // customer_id is required by Razorpay Import Flow (recurring payments).
      // The order is created with customer_id on the backend, so passing it
      // here is both valid and necessary for the payment to link correctly.
      customer_id: options.customer_id || options.customerId,
      recurring: options.recurring,
      checkout_config_id: options.checkout_config_id || options.checkoutConfigId,
      callback_url: options.callbackUrl,
      redirect: options.redirect,
      notes: options.notes,
      prefill: options.prefill,
      theme: options.theme,
      retry: options.retry ?? { enabled: true, max_count: 1 },
      config: options.config,
      modal: {
        ...options.modal,
        ondismiss() {
          options.modal?.ondismiss?.();
          reject(new Error('Payment cancelled'));
        }
      },
      handler(response) {
        resolve(response);
      }
    };
    if (options.method) {
      checkoutOptions.method = options.method;
    }

    const instance = new window.Razorpay(checkoutOptions);

    instance.on('payment.failed', (failure) => {
      const description = failure?.error?.description || 'Payment failed';
      const code = failure?.error?.code ? ` (${failure.error.code})` : '';
      reject(new Error(`${description}${code}`));
    });

    instance.open();
  });
}

export default openRazorpayCheckout;
